import { WarningAmberRounded } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAuth } from "auth/AuthContext";
import { useAuthMachine } from "auth/state/hook";
import AlertDialog from "components/AlertDialog";
import OutlinedButton from "components/buttons/OutlinedButton";
import StyledErrorButton from "components/buttons/StyledErrorButton";
import _delay from "lodash/delay";
import { useState } from "react";
import { Link } from "react-router-dom";
import { CLUSTER_DETAIL_URL } from "utils/routePaths";
import { isWorkerHostingEnvironment } from "utils/saastypes";
import { useActionWithPath, useUserIds } from "utils/query";
import { resolveVersion } from "utils/utils";
import ClusterDetailRow from "./ClusterDetailRow";
import ProtectionToggleRow from "./ProtectionToggleRow";
import { TabPanel } from "./TabPanel";

export default function DetailsTab({
  selectedTabIndex,
  index,
  clusterDetail,
  refetchClusterDetail,
}: {
  selectedTabIndex: number;
  index: number;
  clusterDetail: any;
  refetchClusterDetail: Function;
}) {
  // Is it hosted on any of our accounts?
  const orkesAccount = clusterDetail?.orkesAccount;
  const { authService } = useAuth();
  const [{ isOrkesUser }] = useAuthMachine(authService);

  // Worker Hosting (CLOUD-1193)
  const isWorkerHosting = isWorkerHostingEnvironment(clusterDetail);
  const parentClusterName = clusterDetail?.parentClusterName;
  const hostedWorkerEnvironments = clusterDetail?.hostedWorkerEnvironments;

  const region = clusterDetail?.region;
  const azIds = clusterDetail?.azIds?.join(", ");
  const computeUnitDescription = clusterDetail?.computeUnitDescription;
  const accountId = clusterDetail?.accountId;
  const eksVersion = clusterDetail?.eksVersion;
  const cloudProvider = clusterDetail?.cloudProvider;
  // Multi-region peering (CLOUD-1109)
  const replicationRole = clusterDetail?.replicationRole;
  const peerClusterName = clusterDetail?.peerClusterName;
  const peeringStatus = clusterDetail?.peeringStatus;

  const createdBy = clusterDetail?.createdBy;
  const createdAt = clusterDetail?.createdAt;
  const updatedBy = clusterDetail?.updatedBy;
  const updatedAt = clusterDetail?.updatedAt;

  const isDeletionEnabled = clusterDetail?.deleteProduction;
  const isChangeProtectionEnabled = clusterDetail?.changeProtection;
  // Pair-scoped, so it only means something once the cluster is peered: the value lives on
  // both rows of the pairing and is reset when the pairing is torn down.
  const isAutomaticFailoverEnabled = clusterDetail?.automaticFailover;
  const isPeered = Boolean(replicationRole && peerClusterName);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cancelDelete, setCancelDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmEnableDelete, setConfirmEnableDelete] = useState(false);
  const [isCurrentlyEnablingDeletion, setIsCurrentlyEnablingDeletion] =
    useState(false);
  const [isTogglingChangeProtection, setIsTogglingChangeProtection] =
    useState(false);
  const [isTogglingAutomaticFailover, setIsTogglingAutomaticFailover] =
    useState(false);
  const [confirmEnableAutomaticFailover, setConfirmEnableAutomaticFailover] =
    useState(false);
  const [confirmDisableChangeProtection, setConfirmDisableChangeProtection] =
    useState(false);

  const [typedClusterName, setTypedClusterName] = useState<String>("");

  const { data: userData } = useUserIds([createdBy, updatedBy]);

  const toggleDeletionProtectionAction = useActionWithPath({
    onSuccess: () => {
      setIsCurrentlyEnablingDeletion(false);
      refetchClusterDetail();
    },
    onError: () => {
      setIsCurrentlyEnablingDeletion(false);
      refetchClusterDetail();
    },
  });

  const toggleChangeProtectionAction = useActionWithPath({
    onSuccess: () => {
      setIsTogglingChangeProtection(false);
      refetchClusterDetail();
    },
    onError: () => {
      setIsTogglingChangeProtection(false);
      refetchClusterDetail();
    },
  });

  const toggleAutomaticFailoverAction = useActionWithPath({
    onSuccess: () => {
      setIsTogglingAutomaticFailover(false);
      // Refetch matters more here than for the other two: the server writes BOTH rows of the
      // pair, so this cluster's view is only half of what changed.
      refetchClusterDetail();
    },
    onError: () => {
      setIsTogglingAutomaticFailover(false);
      refetchClusterDetail();
    },
  });

  const deleteEnvironmentAction = useActionWithPath({
    onSuccess: () => {
      refetchClusterDetail();
      setIsSubmitting(false);
    },
    onError: () => {
      refetchClusterDetail();
      setIsSubmitting(false);
    },
  });

  const cancelDeletionAction = useActionWithPath({
    onSuccess: () => {
      _delay(
        function () {
          refetchClusterDetail();
          setIsSubmitting(false);
        },
        10000,
        "later"
      );
    },
    onError: () => {
      refetchClusterDetail();
      setIsSubmitting(false);
    },
  });

  const deployApiGatewayAction = useActionWithPath({
    onSuccess: () => {
      refetchClusterDetail();
    },
    onError: () => {
      refetchClusterDetail();
    },
  });

  function triggerDeployApiGateway() {
    // @ts-ignore
    deployApiGatewayAction.mutate({
      method: "post",
      path: `/admin/patchClusters/deploy_api_gateway_to_existing_cluster?organizationId=${clusterDetail.organizationId}&clusterName=${clusterDetail.name}&cloudProvider=${clusterDetail.cloudProvider}`,
      body: "",
      successMessage: `Triggered API Gateway deployment for ${clusterDetail.name}`,
    });
  }

  function triggerDeletionFlow() {
    if (clusterDetail) {
      setConfirmDelete(false);
      setIsSubmitting(true);
      // @ts-ignore
      deleteEnvironmentAction.mutate({
        method: "post",
        path: `/deleteEnvironment`,
        body: JSON.stringify({
          clusterName: clusterDetail.name,
        }),
        successMessage: `Cluster ${clusterDetail.name} has been scheduled for deletion`,
      });
    }
  }

  function triggerEnableDeletionFlow() {
    setIsCurrentlyEnablingDeletion(true);
    // @ts-ignore
    toggleDeletionProtectionAction.mutate({
      method: "post",
      path: `/setClusterDeletion?clusterName=${
        clusterDetail.name
      }&deleteProduction=${!clusterDetail.deleteProduction}`,
      body: "",
      successMessage: `Deleting has been ${
        isDeletionEnabled ? "disabled" : "enabled"
      } for the cluster ${clusterDetail.name}`,
    });
  }

  function triggerToggleChangeProtection() {
    setIsTogglingChangeProtection(true);
    // @ts-ignore
    toggleChangeProtectionAction.mutate({
      method: "post",
      path: `/setClusterChangeProtection?clusterName=${
        clusterDetail.name
      }&changeProtection=${!clusterDetail.changeProtection}`,
      body: "",
      successMessage: `Change protection has been ${
        isChangeProtectionEnabled ? "disabled" : "enabled"
      } for the cluster ${clusterDetail.name}`,
    });
  }

  function triggerToggleAutomaticFailover() {
    setIsTogglingAutomaticFailover(true);
    // @ts-ignore
    toggleAutomaticFailoverAction.mutate({
      method: "post",
      path: `/setPairAutomaticFailover?clusterName=${
        clusterDetail.name
      }&automaticFailover=${!clusterDetail.automaticFailover}`,
      body: "",
      // Names the PAIR, not this cluster, because that is what actually changed.
      successMessage: `Automatic failover has been ${
        isAutomaticFailoverEnabled ? "DISABLED" : "ENABLED"
      } for the ${clusterDetail.name} / ${peerClusterName} pair`,
    });
  }

  function triggerCancelDeletion() {
    if (clusterDetail) {
      setCancelDelete(false);
      setIsSubmitting(true);
      // @ts-ignore
      cancelDeletionAction.mutate({
        method: "post",
        path: `/cancelDeleteEnvironment`,
        body: JSON.stringify({
          clusterName: clusterDetail.name,
        }),
        successMessage: `Cancellation of deletion request for ${clusterDetail.name} was successful. Please wait a few seconds to get the updated cluster state`,
      });
    }
  }

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg")); //1439px

  return (
    <TabPanel value={selectedTabIndex} index={index}>
      <AlertDialog
        textTitle={"Deleting Confirmation"}
        textContent={
          <>
            Please confirm if you want to proceed to delete the cluster&nbsp;
            <strong className="danger-text">{clusterDetail.name}</strong>. This
            action cannot be undone. All data will be permanently lost.
          </>
        }
        open={confirmDelete}
        setOpen={() => setConfirmDelete(false)}
        handleConfirm={() => {
          triggerDeletionFlow();
        }}
        isInputConfirmation
        valueToBeDeleted={clusterDetail.name}
      />
      <AlertDialog
        textTitle={""}
        textContent={
          // AlertDialog renders textContent inside a <p> (DialogContentText), so
          // this must stay inline content — no Box/Typography, which render div/p.
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "flex-start", gap: 1 }}
          >
            <WarningAmberRounded color="error" sx={{ flexShrink: 0 }} />
            <span>
              Delete Protection will be{" "}
              <strong className="danger-text">DISABLED</strong>. You are
              enabling the option to{" "}
              <strong className="danger-text">DELETE</strong> this cluster.
              Please type in the cluster name to enable deletion:
            </span>
          </Box>
        }
        open={confirmEnableDelete}
        setOpen={setConfirmEnableDelete}
        handleConfirm={() => {
          setConfirmEnableDelete(false);
          triggerEnableDeletionFlow();
        }}
        isConfirmDisabled={typedClusterName !== clusterDetail.name}
        // Keeps its own TextField/typedClusterName rather than AlertDialog's
        // isInputConfirmation gate (unlike the change-protection dialog below)
        // since this flow is untouched deliberately; valueToBeDeleted here only
        // adds the "Please type <name> to confirm" hint text, it does not
        // affect the gate.
        valueToBeDeleted={clusterDetail.name}
      >
        <FormControl
          style={{
            marginTop: 16,
            width: "100%",
          }}
        >
          <TextField
            id="confirm-cluster-name-input-field"
            label="Cluster Name"
            placeholder="Cluster Name"
            value={typedClusterName}
            onChange={(ev) => {
              setTypedClusterName(ev.target.value);
            }}
          />
        </FormControl>
      </AlertDialog>
      <AlertDialog
        textTitle={"Cancel Cluster Deletion"}
        textContent={`You are now cancelling the request for deletion of your cluster ${
          clusterDetail?.name ?? ""
        }`}
        open={cancelDelete}
        setOpen={setCancelDelete}
        handleConfirm={() => triggerCancelDeletion()}
      />
      <AlertDialog
        textTitle={""}
        textContent={
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "flex-start", gap: 1 }}
          >
            <WarningAmberRounded color="error" sx={{ flexShrink: 0 }} />
            <span>
              Change Protection will be{" "}
              <strong className="danger-text">DISABLED</strong>. You are
              enabling changes. Please type in the cluster name to disable
              change protection:
            </span>
          </Box>
        }
        open={confirmDisableChangeProtection}
        setOpen={setConfirmDisableChangeProtection}
        handleConfirm={() => {
          triggerToggleChangeProtection();
        }}
        isInputConfirmation
        valueToBeDeleted={clusterDetail.name}
        // Matches the delete-protection dialog above, where the hint sits
        // below its (differently-implemented) input.
        hintAfterInput
      />
      <AlertDialog
        textTitle={"Allow automatic failover?"}
        textContent={
          <Box>
            <span>
              Automatic Failover will be{" "}
              <strong className="danger-text">ENABLED</strong> for the{" "}
              <strong>
                {clusterDetail.name} / {peerClusterName}
              </strong>{" "}
              pair. If the primary is detected as down, the replica will be
              promoted <strong>automatically, with no human involved</strong> —
              a rolling restart of the promoted cluster, and in-flight tasks
              re-dispatched. This setting belongs to the pairing, so it applies
              to both clusters, and it is reset if the pair is unpeered. Type
              the cluster name to enable automatic failover:
            </span>
          </Box>
        }
        open={confirmEnableAutomaticFailover}
        setOpen={setConfirmEnableAutomaticFailover}
        handleConfirm={() => {
          triggerToggleAutomaticFailover();
        }}
        isInputConfirmation
        valueToBeDeleted={clusterDetail.name}
        hintAfterInput
      />
      <Box
        sx={{
          display: "flex",
          flexFlow: "row wrap",
          borderBottom: "1px solid lightgrey",
          padding: "16px",
        }}
      >
        <Box sx={{ display: "flex", gap: 25, width: "100%" }}>
          <Box
            sx={{
              width: "60%",
              minWidth: 0,
              display: "flex",
              flexDirection: isLargeScreen ? "row" : "column",
              gap: isLargeScreen ? 10 : 0,
              overflow: "hidden",
            }}
          >
            <Box sx={{ flex: "0 0 50%", minWidth: 0 }}>
              <ClusterDetailRow
                title="Cloud Environment:"
                detail={cloudProvider}
              />
              {region && <ClusterDetailRow title="Region:" detail={region} />}
              {replicationRole && (
                <ClusterDetailRow
                  title="Replication status:"
                  detail={`${replicationRole}${
                    peeringStatus ? ` (${peeringStatus})` : ""
                  }`}
                />
              )}
              {replicationRole && peerClusterName && (
                <ClusterDetailRow
                  title="Replication peer:"
                  detail={
                    // Its own row: cluster names contain hyphens, which browsers treat as line-break
                    // opportunities, so "aa-region-a" was splitting mid-name when it shared a row with
                    // the role and status. A short value in its own row cannot wrap.
                    <Link to={`${CLUSTER_DETAIL_URL.ROOT}/${peerClusterName}`}>
                      {peerClusterName}
                    </Link>
                  }
                />
              )}
              {/* The attachment is rendered from both sides on purpose: without the
                  reverse ("Remote delegated workers") row, a cluster that has delegated all
                  system tasks to a remote worker environment looks unchanged in the UI and
                  the relationship is only discoverable from the worker environment's
                  side. */}
              {isWorkerHosting && parentClusterName && (
                <ClusterDetailRow
                  title="Attached to:"
                  detail={
                    <Link
                      to={`${CLUSTER_DETAIL_URL.ROOT}/${parentClusterName}`}
                    >
                      {parentClusterName}
                    </Link>
                  }
                />
              )}
              {!isWorkerHosting && hostedWorkerEnvironments?.length > 0 && (
                <ClusterDetailRow
                  title="Remote workers:"
                  detail={hostedWorkerEnvironments.map(
                    (workerEnvironment: any, index: number) => (
                      <span key={workerEnvironment.id}>
                        {index > 0 && ", "}
                        <Link
                          to={`${CLUSTER_DETAIL_URL.ROOT}/${workerEnvironment.name}`}
                        >
                          {workerEnvironment.name}
                        </Link>
                      </span>
                    )
                  )}
                />
              )}
              {isWorkerHosting && clusterDetail?.namespace && (
                <ClusterDetailRow
                  title="Namespace:"
                  detail={clusterDetail.namespace}
                />
              )}
              {azIds && (!orkesAccount || isOrkesUser) && (
                <ClusterDetailRow title="Availability Zones:" detail={azIds} />
              )}
              {computeUnitDescription && (
                <ClusterDetailRow
                  title="Capacity:"
                  detail={`${computeUnitDescription} OCUs`}
                />
              )}
              {accountId && (!orkesAccount || isOrkesUser) && (
                <ClusterDetailRow
                  title={
                    cloudProvider === "GCP" ? "Project ID:" : "Account ID:"
                  }
                  detail={accountId}
                />
              )}
              {eksVersion && (!orkesAccount || isOrkesUser) && (
                <ClusterDetailRow title="EKS Version:" detail={eksVersion} />
              )}
              {!isWorkerHosting &&
                (cloudProvider === "AWS" ||
                  cloudProvider === "AZURE" ||
                  cloudProvider === "GCP") && (
                  <ClusterDetailRow
                    title="API Gateway:"
                    detail={
                      <Typography
                        component="span"
                        sx={{
                          color: clusterDetail?.enableApiGateway
                            ? "success.main"
                            : "text.secondary",
                          fontWeight: clusterDetail?.enableApiGateway
                            ? "medium"
                            : "normal",
                        }}
                      >
                        {clusterDetail?.enableApiGateway
                          ? "Enabled"
                          : "Disabled"}
                      </Typography>
                    }
                  />
                )}
            </Box>
            <Box sx={{ minWidth: 0, flex: "1 1 45%" }}>
              {!isWorkerHosting && (
                <ClusterDetailRow
                  title="Conductor Server:"
                  detail={
                    resolveVersion(clusterDetail?.appVersions?.conductor) ||
                    "Not Available"
                  }
                  isScrollable
                />
              )}

              <ClusterDetailRow
                title="Conductor Workers:"
                detail={
                  resolveVersion(clusterDetail?.appVersions?.workers) ||
                  "Not Available"
                }
                isScrollable
              />

              <ClusterDetailRow
                title="Orkes Agent:"
                detail={
                  resolveVersion(clusterDetail?.appVersions?.agent) ||
                  "Not Available"
                }
                isScrollable
              />

              {!isWorkerHosting && (
                <ClusterDetailRow
                  title="Metrics Proxy:"
                  detail={
                    resolveVersion(clusterDetail?.appVersions?.metricsProxy) ||
                    "Not Available"
                  }
                  isScrollable
                />
              )}

              {clusterDetail?.appVersions?.mcpWorker && (
                <ClusterDetailRow
                  title="MCP Workers:"
                  detail={
                    resolveVersion(clusterDetail?.appVersions?.mcpWorker) ||
                    "Not Available"
                  }
                  isScrollable
                />
              )}

              {clusterDetail?.appVersions?.orkesAssistant && (
                <ClusterDetailRow
                  title="Orkes Assistant:"
                  detail={
                    resolveVersion(
                      clusterDetail?.appVersions?.orkesAssistant
                    ) || "Not Available"
                  }
                  isScrollable
                />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              width: "40%",
              minWidth: 0,
            }}
          >
            {createdBy && (
              <ClusterDetailRow
                title="Created By:"
                detail={
                  userData && userData[createdBy]
                    ? userData[createdBy].userEmail
                    : "Unknown"
                }
                isScrollable
              />
            )}
            {createdAt && (
              <ClusterDetailRow
                title="Created At:"
                detail={new Date(createdAt).toLocaleString()}
              />
            )}
            {updatedBy && (
              <ClusterDetailRow
                title="Last Updated By:"
                detail={
                  userData && userData[updatedBy]
                    ? userData[updatedBy].userEmail
                    : "Unknown"
                }
                isScrollable
              />
            )}
            {updatedAt && (
              <ClusterDetailRow
                title="Last Updated at:"
                detail={new Date(updatedAt).toLocaleString()}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
          }}
        >
          {!isDeletionEnabled && (
            <ProtectionToggleRow
              id="delete-protection-switch-enabled-btn"
              label="Delete Protection"
              isProtected
              onToggle={() => {
                setConfirmEnableDelete(true);
              }}
            />
          )}
          {isDeletionEnabled &&
            clusterDetail?.state !== "SCHEDULE_DELETION" && (
              <ProtectionToggleRow
                id="delete-protection-switch-disabled-btn"
                label="Delete Protection"
                isProtected={false}
                disabled={isCurrentlyEnablingDeletion}
                onToggle={() => {
                  triggerEnableDeletionFlow();
                }}
              />
            )}
          <ProtectionToggleRow
            id="change-protection-switch-btn"
            label="Change Protection"
            isProtected={isChangeProtectionEnabled}
            disabled={isTogglingChangeProtection}
            onToggle={() => {
              if (isChangeProtectionEnabled) {
                setConfirmDisableChangeProtection(true);
              } else {
                triggerToggleChangeProtection();
              }
            }}
          />
          {/* Only for a peered cluster, unlike the two above: auto-promotion consent belongs to
              the PAIRING, so on a standalone cluster there is nothing for it to describe. */}
          {isPeered && (
            <ProtectionToggleRow
              id="automatic-failover-switch-btn"
              label="Automatic Failover"
              // NOTE the prop name: ProtectionToggleRow calls this `isProtected` because its
              // other two users are protections. Here ON means the capability is ACTIVE, not
              // that something is protected. Renaming the shared prop is churn for another day;
              // the rendered position is correct.
              isProtected={isAutomaticFailoverEnabled}
              disabled={isTogglingAutomaticFailover}
              onToggle={() => {
                // Confirms in the OPPOSITE direction to its two neighbours. For a protection the
                // risky move is switching it off; for a capability it is switching it ON -- that
                // is what allows a promotion with nobody watching.
                if (!isAutomaticFailoverEnabled) {
                  setConfirmEnableAutomaticFailover(true);
                } else {
                  triggerToggleAutomaticFailover();
                }
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              marginTop: "8px",
            }}
          >
            {!isWorkerHosting && (
              <OutlinedButton
                onClick={() => triggerDeployApiGateway()}
                disabled={isSubmitting}
                variant="contained"
                style={{ marginRight: 16 }}
              >
                {deployApiGatewayAction.isLoading && (
                  <CircularProgress
                    size={20}
                    style={{ marginRight: 8, color: "inherit" }}
                  />
                )}
                Deploy API Gateway
              </OutlinedButton>
            )}
            {clusterDetail?.state !== "SCHEDULE_DELETION" && (
              <StyledErrorButton
                sx={{
                  "&.Mui-disabled": {
                    color: "red",
                  },
                }}
                onClick={() => {
                  setConfirmDelete(true);
                }}
                disabled={
                  !isDeletionEnabled ||
                  clusterDetail.state !== "PROVISIONED" ||
                  isSubmitting
                }
                color="error"
                style={{
                  marginRight: 16,
                }}
                variant="contained"
                id="delete-cluster-btn"
              >
                {isSubmitting && (
                  <CircularProgress
                    size={20}
                    style={{ marginRight: 8, color: "gray" }}
                  />
                )}
                {isWorkerHosting
                  ? "Delete Worker Environment"
                  : "Delete Cluster"}
              </StyledErrorButton>
            )}
            {clusterDetail?.state === "SCHEDULE_DELETION" && (
              <OutlinedButton
                onClick={() => {
                  setCancelDelete(true);
                }}
                disabled={isSubmitting}
                variant="contained"
              >
                {isSubmitting && (
                  <CircularProgress
                    size={20}
                    style={{ marginRight: 8, color: "inherit" }}
                  />
                )}
                Cancel Scheduled Deletion
              </OutlinedButton>
            )}
          </Box>
        </Box>
      </Box>
    </TabPanel>
  );
}
